import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { RequirePermission } from '../../../shared/components/RequirePermission';
import { PEOPLE_COCKPIT_VIEW } from '../permissions';
import {
  fetchPeopleCockpitData,
  PeopleCockpitForbiddenError,
  type PeopleCockpitData,
} from '../services/peopleCockpitApi';
import cockpitHtml from './peopleCockpit.html?raw';

/**
 * People Cockpit — attrition (rolling 12M, by department), forward-looking attrition and
 * probation success, for People leadership.
 *
 * The page itself is the validated prototype (`peopleCockpit.html`, next to this file), rendered in a
 * sandboxed iframe (`allow-scripts` only: no same-origin, so it cannot read this app's storage or
 * Firebase session). This component fetches the aggregates with the user's Firebase token and
 * posts them in; the iframe posts back its height and Refresh clicks.
 *
 * ⚠️ Access is ONLY the named overrides in proxy-server/lib/rbac.js#PEOPLE_COCKPIT_USER_EMAILS.
 * The permission is deliberately not on the core-admin role, so a core admin passes the `strict`
 * route guard (it always admits core admins) but gets a 403 from the proxy — handled below as the
 * same "access restricted" screen.
 */

function PeopleCockpitRestricted() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
          <Lock className="h-6 w-6 text-zinc-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Access restricted</h2>
          <p className="mt-1 text-sm text-zinc-500">
            The People Cockpit shows confidential attrition and probation figures, so access is
            limited to named People leads. If you need it, ask Lisa Haunolder.
          </p>
        </div>
      </div>
    </div>
  );
}

function PeopleCockpitFrame() {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [data, setData] = useState<PeopleCockpitData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [height, setHeight] = useState(2400);
  const [frameReady, setFrameReady] = useState(false);

  const handleError = useCallback((err: unknown) => {
    if (err instanceof PeopleCockpitForbiddenError) setForbidden(true);
    else setError(err instanceof Error ? err.message : 'Could not load the People Cockpit.');
  }, []);

  // Refresh (from the iframe's button): bypasses the proxy cache.
  const load = useCallback(
    (fresh: boolean) => {
      setError(null);
      fetchPeopleCockpitData(fresh).then(setData, handleError);
    },
    [handleError]
  );

  // Initial load: state is only set in the promise callbacks, never synchronously in the effect.
  useEffect(() => {
    let alive = true;
    fetchPeopleCockpitData(false).then(
      (next) => {
        if (alive) setData(next);
      },
      (err: unknown) => {
        if (alive) handleError(err);
      }
    );
    return () => {
      alive = false;
    };
  }, [handleError]);

  // Messages from the iframe: its content height, and Refresh clicks.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.source !== frameRef.current?.contentWindow) return;
      const msg = event.data as { type?: string; height?: number } | null;
      if (msg?.type === 'pc-height' && typeof msg.height === 'number') {
        setHeight(Math.max(600, Math.min(20000, Math.round(msg.height))));
      } else if (msg?.type === 'pc-refresh') {
        void load(true);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [load]);

  // Post the rows in whenever they (or the iframe) become available. The sandboxed frame has an
  // opaque origin, so '*' is the only possible target; the message goes to this frame's window only.
  useEffect(() => {
    if (!frameReady) return;
    const target = frameRef.current?.contentWindow;
    if (!target) return;
    const payload = data ?? (error ? { main: null, forward: null, probation: null, errors: { main: error, forward: error, probation: error } } : null);
    if (!payload) return;
    // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration, wildcard-postmessage-configuration -- the target is our own sandboxed iframe (opaque origin, so no concrete origin exists); the message goes only to frameRef's contentWindow, which renders bundled HTML.
    target.postMessage({ type: 'pc-data', payload }, '*');
  }, [data, error, frameReady]);

  const srcDoc = useMemo(
    () =>
      '<!doctype html><html lang="en" data-theme="light"><head><meta charset="utf-8">' +
      '<meta name="viewport" content="width=device-width, initial-scale=1"></head><body>' +
      cockpitHtml +
      '</body></html>',
    []
  );

  if (forbidden) return <PeopleCockpitRestricted />;

  return (
    <iframe
      ref={frameRef}
      title="People Cockpit"
      srcDoc={srcDoc}
      sandbox="allow-scripts"
      onLoad={() => setFrameReady(true)}
      className="block w-full border-0"
      style={{ height }}
    />
  );
}

export default function PeopleCockpitView() {
  return (
    <RequirePermission strict permission={PEOPLE_COCKPIT_VIEW} fallback={<PeopleCockpitRestricted />}>
      <PeopleCockpitFrame />
    </RequirePermission>
  );
}
